import React, { useEffect, useState, useRef, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
  Avatar,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  List,
  ListItem,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Share as ShareIcon,
  PhotoCamera as PhotoCameraIcon, // Add this icon
  Edit as EditIcon, // Import Edit icon
  Email as EmailIcon
} from '@mui/icons-material';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { getCoverArtUrl, getAudioFileUrl } from '../../utils/urlHelpers';
import EmailPromotionDialog from '../../components/email/EmailPromotionDialog';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const ReleasePage = () => {
  const { releaseId } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [release, setRelease] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [comments, setComments] = useState([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [promoLink, setPromoLink] = useState('');
  const [isExistingPromoLink, setIsExistingPromoLink] = useState(false);
  const [isSingleTrack, setIsSingleTrack] = useState(false);
  
  // Comment sharing state
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  
  // Email promotion state
  const [emailPromotionOpen, setEmailPromotionOpen] = useState(false);
  
  const audioRef = useRef(null);
  
  useEffect(() => {
    const fetchReleaseData = async () => {
      try {
        setLoading(true);
        setError(null);
        // Add auth token to request header if available
        const token = localStorage.getItem('token');
        const config = token ? {
          headers: { Authorization: `Bearer ${token}` }
        } : {};
        
        // Try fetching as a release first
        try {
          const releaseRes = await axios.get(`${API_URL}/releases/${releaseId}`, config);
          const releaseData = releaseRes.data;
          setRelease(releaseData);
          setIsSingleTrack(false);
          
          // Check if user is the owner here where we have both user and release data
          if (user && releaseData.artist && user._id === releaseData.artist._id) {
            setIsOwner(true);
          }
        } catch (releaseErr) {
          // If release not found, try fetching as a track
          if (releaseErr.response?.status === 404) {
            try {
              const trackRes = await axios.get(`${API_URL}/tracks/${releaseId}`, config);
              const trackData = trackRes.data;
              
              // Convert track to a release-like format for consistent UI display
              const trackAsRelease = {
                _id: trackData._id,
                title: trackData.title,
                artist: trackData.artist,
                coverArt: trackData.coverArt,
                genre: trackData.genre,
                type: 'single',
                description: trackData.description,
                releaseDate: trackData.releaseDate || trackData.createdAt,
                createdAt: trackData.createdAt,
                tags: trackData.tags || [],
                isPublic: trackData.isPublic,
                downloadable: trackData.downloadable,
                tracks: [trackData], // Wrap the track in an array for consistent handling
              };
              
              setRelease(trackAsRelease);
              setCurrentTrack(trackData); // Auto-select the track for playback controls
              setIsSingleTrack(true);
              
              // Check if user is the owner here where we have both user and track data
              if (user && trackData.artist && user._id === trackData.artist._id) {
                setIsOwner(true);
              }
            } catch (trackErr) {
              throw new Error(trackErr.response?.data?.message || 'Item not found');
            }
          } else {
            throw new Error(releaseErr.response?.data?.message || 'Failed to load release');
          }
        }
        
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load the requested item');
        setLoading(false);
      }
    };
    
    fetchReleaseData();
  }, [releaseId, user]);
  
  // Separate effect for fetching comments after release or track info is loaded
  useEffect(() => {
    // Only fetch comments if we have release info and know the type (single or multi-track)
    if (!release) return;
    
    const fetchComments = async () => {
      try {
        // Add auth token to request header if available
        const token = localStorage.getItem('token');
        const config = token ? {
          headers: { Authorization: `Bearer ${token}` }
        } : {};
        
        // Determine appropriate comments endpoint based on whether this is a single track or release
        const commentsEndpoint = isSingleTrack 
          ? `/feedback/track/${releaseId}` 
          : `/feedback/release/${releaseId}`;
        
        const commentsRes = await axios.get(`${API_URL}${commentsEndpoint}`, config);
        
        if (Array.isArray(commentsRes.data) && commentsRes.data.length > 0) {
          // Comments found and loaded
        } else {
          // No comments found
        }
        
        setComments(commentsRes.data);
      } catch (commentsErr) {
        setComments([]);
      }
    };
    
    fetchComments();
    
    // Check if user is the owner (moved from previous effect)
    if (user && release && release.artist._id === user._id) {
      setIsOwner(true);
    }
  }, [releaseId, release, isSingleTrack, user]);
  
  // Set up audio player once release data is loaded
  useEffect(() => {
    if (!release) return;
    
    // For single tracks, set the current track but don't auto-play
    if (isSingleTrack && release.tracks.length > 0) {
      setCurrentTrack(release.tracks[0]);
      
      // Pre-initialize the audio element for the single track
      // This makes it ready to play when the user clicks the play button
      const audio = new Audio(getAudioFileUrl(release.tracks[0].audioFile));
      audio.preload = "metadata"; // Load metadata (duration, etc.) but not the full audio
      audioRef.current = audio;
    }
  }, [release, isSingleTrack]);
  
  // Define handlePlayTrack before the useEffect that depends on it
  const handlePlayTrack = useCallback(async (track) => {
    if (currentTrack?._id === track._id) {
      // Toggle play/pause for current track
      if (playing) {
        audioRef.current?.pause();
      } else {
        audioRef.current?.play();
      }
      setPlaying(!playing);
      return;
    }
    
    // Stop current track if playing
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    // Create and play new audio
    const audio = new Audio(getAudioFileUrl(track.audioFile));
    audioRef.current = audio;
    audio.play();
    setPlaying(true);
    setCurrentTrack(track);
    
    // Update play count
    try {
      await axios.post(`${API_URL}/tracks/${track._id}/play`);
    } catch (err) {
    }
  }, [currentTrack, playing]);
  
  // Audio player effects and handlers
  useEffect(() => {
    if (!audioRef.current || !currentTrack) return;
    const audio = audioRef.current;
    
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };
    
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };
    
    const handleEnded = () => {
      setPlaying(false);
      
      // Auto-play next track if available
      if (release && !isSingleTrack) {
        const currentIndex = release.tracks.findIndex(t => t._id === currentTrack._id);
        if (currentIndex < release.tracks.length - 1) {
          handlePlayTrack(release.tracks[currentIndex + 1]);
        }
      }
    };
    
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrack, release, isSingleTrack, handlePlayTrack]);
  
  const handleSliderChange = (event, newValue) => {
    if (!audioRef.current) return;
    const time = (newValue / 100) * duration;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };
  
  const generatePromoLink = async () => {
    try {
      let res;
      
      // Use the appropriate API endpoint based on whether this is a single track or a release
      if (isSingleTrack) {
        res = await axios.post(
          `${API_URL}/tracks/${releaseId}/promoLink`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        // For single tracks, we directly use the trackId in the promo URL
        const baseUrl = window.location.origin;
        setPromoLink(`${baseUrl}/promo/track/${releaseId}`);
        setIsExistingPromoLink(res.data.isExisting || false);
      } else {
        res = await axios.post(
          `${API_URL}/releases/${releaseId}/promoLink`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        // For releases, we use the promo code generated by the server
        const baseUrl = window.location.origin;
        setPromoLink(`${baseUrl}/promo/release/${res.data.promoCode}`);
        setIsExistingPromoLink(res.data.isExisting || false);
      }
      
      setShareDialogOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate promo link');
    }
  };

  const revokePromoLink = async () => {
    try {
      let res;
      
      // Use the appropriate API endpoint based on whether this is a single track or a release
      if (isSingleTrack) {
        res = await axios.post(
          `${API_URL}/tracks/${releaseId}/revokePromoLink`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        // For single tracks, we directly use the trackId in the promo URL
        const baseUrl = window.location.origin;
        setPromoLink(`${baseUrl}/promo/track/${releaseId}`);
      } else {
        res = await axios.post(
          `${API_URL}/releases/${releaseId}/revokePromoLink`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        // For releases, we use the promo code generated by the server
        const baseUrl = window.location.origin;
        setPromoLink(`${baseUrl}/promo/release/${res.data.promoCode}`);
      }
      
      setIsExistingPromoLink(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to revoke promo link');
    }
  };
  
  const handleShareComment = (comment) => {
    setSelectedComment(comment);
    setCommentDialogOpen(true);
  };
  
  // Updated function to handle Instagram Story generation
  const shareAsInstagramStory = async (commentId) => { // Make async
    if (!commentId) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }
      
      // Fetch the image data as a blob using axios with Authorization header
      const response = await axios.get(`${API_URL}/social/instagram-story/${commentId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        responseType: 'blob' // Important: Get response as a Blob
      });
      
      // Create an object URL from the blob
      const imageUrl = URL.createObjectURL(response.data);
      
      // Open the object URL in a new tab
      window.open(imageUrl, '_blank');
      
      // Optional: Revoke the object URL after a delay
      setTimeout(() => URL.revokeObjectURL(imageUrl), 100);

      // Close the dialog
      setCommentDialogOpen(false);
      setSelectedComment(null);

    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate Instagram Story image');
      // Keep the dialog open if there was an error
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  if (!release) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Item not found</Alert>
      </Box>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Grid container spacing={4}>
        {/* Release/Track Info Section */}
        <Grid item xs={12} md={8}>
          {/* Cover and Basic Info */}
          <Paper sx={{ mb: 4, overflow: 'hidden' }}>
            <Grid container>
              <Grid item xs={12} sm={4}>
                <Box 
                  sx={{
                    width: '100%',
                    paddingTop: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Box
                    component="img"
                    src={getCoverArtUrl(release.coverArt)}
                    alt={release.title}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={8}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h4" gutterBottom>{release.title}</Typography>
                  
                  {/* Artist & Label Info */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ mr: 1 }}>
                        Artist(s):
                      </Typography>
                      <Typography variant="subtitle1">
                        {release.tracks.reduce((artists, track) => {
                          // Add main artists
                          const mainArtists = track.featuredArtists?.filter(a => a.role === 'main') || [];
                          mainArtists.forEach(artist => {
                            if (artist.name && !artists.includes(artist.name)) {
                              artists.push(artist.name);
                            }
                          });
                          // Add featured artists
                          track.featuredArtists?.filter(a => a.role !== 'main').forEach(artist => {
                            if (!artists.includes(artist.name)) {
                              artists.push(artist.name);
                            }
                          });
                          return artists;
                        }, []).join(', ') || release.artist.artistName || release.artist.username}
                      </Typography>
                    </Box>
                    
                    {release.label && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" color="text.secondary" sx={{ mr: 1 }}>
                          Label:
                        </Typography>
                        <Typography variant="subtitle1">
                          {release.label}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle1" color="text.secondary" sx={{ mr: 1 }}>
                        Genre:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        <Chip 
                          label={release.primaryGenre} 
                          color="primary" 
                          size="small"
                        />
                        {release.secondaryGenres && release.secondaryGenres.map(genre => (
                          <Chip 
                            key={genre} 
                            label={genre} 
                            size="small" 
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                  
                  {release.tags && release.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {release.tags.map(tag => (
                        <Chip 
                          key={tag} 
                          label={tag} 
                          size="small" 
                          variant="outlined" 
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
            
            {/* Bio/Description Section */}
            {release.description && (
              <Box sx={{ p: 3, pt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {release.description}
                </Typography>
              </Box>
            )}
            
            {/* Single Track Player (visible only for singles) */}
            {isSingleTrack && currentTrack && (
              <Box sx={{ p: 3, pt: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <IconButton 
                    onClick={() => handlePlayTrack(currentTrack)} 
                    color="primary"
                    size="large"
                  >
                    {playing ? <PauseIcon /> : <PlayIcon />}
                  </IconButton>
                  
                  <Box sx={{ flex: 1, mx: 2 }}>
                    <Slider
                      value={(currentTime / duration) * 100 || 0}
                      onChange={handleSliderChange}
                      sx={{
                        '& .MuiSlider-thumb': {
                          width: 12,
                          height: 12,
                        },
                        '& .MuiSlider-track': {
                          height: 4,
                        },
                        '& .MuiSlider-rail': {
                          height: 4,
                        },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(currentTime)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTime(duration)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            
            {/* Track List (visible for multi-track releases) */}
            {!isSingleTrack && (
              <>
                <Typography variant="h6" sx={{ px: 3 }} gutterBottom>
                  Tracks
                </Typography>
                <Box sx={{ px: 2, pb: 2 }}>
                  <List sx={{ p: 2 }}>
                    {release.tracks.map((track, index) => (
                      <ListItem
                        key={track._id}
                        sx={{
                          bgcolor: currentTrack?._id === track._id ? 'action.selected' : 'background.paper',
                          mb: 1,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': {
                            bgcolor: currentTrack?._id === track._id ? 'action.selected' : 'action.hover',
                          },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2
                        }}
                      >
                      <IconButton 
                        onClick={() => handlePlayTrack(track)}
                        color={currentTrack?._id === track._id ? 'primary' : 'default'}
                      >
                        {currentTrack?._id === track._id && playing ? 
                          <PauseIcon /> : <PlayIcon />}
                      </IconButton>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography noWrap>
                          {`${(index + 1).toString().padStart(2, '0')}. ${track.title}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {/* Show main artists first */}
                          {track.featuredArtists?.filter(a => a.role === 'main').map(artist => artist.name).join(', ') ||
                           track.artist.artistName || 
                           track.artist.username}
                          {/* Show featured artists */}
                          {track.featuredArtists?.filter(a => a.role !== 'main').map((artist, idx) => (
                            <span key={idx}>
                              {idx === 0 ? ' feat. ' : ', '}
                              {artist.name}
                            </span>
                          ))}
                          {' • '}{track.playCount || 0} plays
                        </Typography>
                      </Box>
                      {currentTrack?._id === track._id && (
                        <Box sx={{ width: '40%', mx: 2 }}>
                          <Slider
                            size="small"
                            value={(currentTime / duration) * 100 || 0}
                            onChange={handleSliderChange}
                            sx={{
                              '& .MuiSlider-thumb': {
                                width: 12,
                                height: 12,
                              }
                            }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(currentTime)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(duration)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </ListItem>
                  ))}
                  </List>
                </Box>
              </>
            )}
          </Paper>
          
          {/* Comments Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Comments
            </Typography>
            {comments.length > 0 ? (
              comments.map(comment => (
                <Box 
                  key={comment._id} 
                  sx={{ 
                    mb: 2, 
                    p: 2, 
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative' // Added to support absolute positioning
                  }}
                >
                  {/* Share Feedback button moved to top-right */}
                  {isOwner && (
                    <Box sx={{ 
                      position: 'absolute',
                      top: 10,
                      right: 10
                    }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ShareIcon />}
                        onClick={() => handleShareComment(comment)}
                        sx={{ borderRadius: 1 }}
                      >
                        Share Feedback
                      </Button>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2 }}>
                        {(comment.user?.username || comment.guestName || 'A')[0].toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">
                          {comment.user?.username || comment.guestName || 'Anonymous'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(comment.createdAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {comment.text}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      Rating:
                    </Typography>
                    <Rating 
                      value={comment.rating} 
                      readOnly 
                      precision={0.5} 
                      size="small" 
                      sx={{
                        '& .MuiRating-iconFilled': {
                          color: '#a187f2',
                        },
                        '& .MuiRating-iconHover': {
                          color: '#a187f2',
                        }
                      }}
                    />
                  </Box>
                  
                  {!isSingleTrack && comment.favoriteTrack && (
                    <Typography variant="body2" color="text.secondary">
                      Favorite Track: {comment.favoriteTrack && typeof comment.favoriteTrack === 'object' 
                        ? comment.favoriteTrack.title 
                        : release.tracks.find(t => t._id === comment.favoriteTrack)?.title || 'Not specified'}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" align="center">
                No comments yet.
              </Typography>
            )}
          </Paper>
        </Grid>
        
        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {isSingleTrack ? 'Track Stats' : 'Release Stats'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2">Total Streams:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {isSingleTrack 
                  ? (currentTrack?.playCount || 0)
                  : release.tracks.reduce((sum, track) => sum + (track.playCount || 0), 0)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body2">Total Downloads:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {isSingleTrack 
                  ? (currentTrack?.downloadCount || 0)
                  : release.tracks.reduce((sum, track) => sum + (track.downloadCount || 0), 0)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Released:</Typography>
              <Typography variant="body2" fontWeight="bold">
                {new Date(release.releaseDate || release.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
            
            {/* Action Buttons */}
            <Stack spacing={2} sx={{ mt: 3 }}>
              {user && user._id === release.artist._id && (
                <>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<ShareIcon />}
                    onClick={generatePromoLink}
                  >
                    {isSingleTrack ? 'Share Track' : 'Share Release'}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<EmailIcon />}
                    onClick={() => setEmailPromotionOpen(true)}
                    color="primary"
                  >
                    Send Email Promotion
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`/releases/${release._id}/edit`)}
                    color="secondary"
                  >
                    Edit Release
                  </Button>
                </>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isSingleTrack ? 'Share Track' : 'Share Release'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Share this link to get feedback on your {isSingleTrack ? 'track' : 'release'}:
          </Typography>
          {isExistingPromoLink && (
            <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
              ✓ Using existing promo link (same as before)
            </Typography>
          )}
          <TextField
            fullWidth
            variant="outlined"
            value={promoLink}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(promoLink);
                  }}
                  edge="end"
                >
                  <ShareIcon />
                </IconButton>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={revokePromoLink}
            color="warning"
            variant="outlined"
            size="small"
          >
            Revoke & Generate New
          </Button>
          <Button onClick={() => setShareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Share Comment Dialog */}
      <Dialog open={commentDialogOpen} onClose={() => setCommentDialogOpen(false)}>
        <DialogTitle>Share Feedback as Instagram Story</DialogTitle> {/* Updated Title */}
        <DialogContent>
          {selectedComment && (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Generate an Instagram Story image for this feedback from {selectedComment.isGuest
                  ? selectedComment.guestName
                  : (selectedComment.user?.artistName || selectedComment.user?.username)}:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="body2">{selectedComment.text}</Typography>
              </Paper>
              {/* Removed Share Link Section */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
                Generate Image:
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<PhotoCameraIcon />} // Changed Icon for clarity
                  onClick={() => shareAsInstagramStory(selectedComment._id)}
                  color="secondary" // Changed color for emphasis
                >
                  Generate Story Image
                </Button>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      
      {/* Email Promotion Dialog */}
      <EmailPromotionDialog
        open={emailPromotionOpen}
        onClose={() => setEmailPromotionOpen(false)}
        release={release}
      />
    </Container>
  );
};

export default ReleasePage;